import { getAnalysisPanelTheme } from '../../theme/themeStyles';
import { useTheme } from '../../theme/ThemeContext';

type FieldReadingsProps = {
  ndviSeries: Array<{ date: string; ndvi: number; cloud_percentage?: number }>;
  llm: {
    growth_stage_periods?: Array<{ stage: string; tentative_start_date: string; tentative_end_date: string | null }>;
    chart_series?: Array<{ x: string; y: number; growth_stage?: string }>;
  } | null;
};

function cardClass(isDark: boolean) {
  return `rounded-2xl border ${isDark ? 'border-[#2d3748] bg-[#1c2432]' : 'border-[#e5e7eb] bg-white'}`;
}

export default function CropCalendarFieldReadings({
  ndviSeries,
  llm,
}: FieldReadingsProps) {
  const { isDark } = useTheme();
  const ap = getAnalysisPanelTheme(isDark);

  return (
    <details className={cardClass(isDark)}>
      <summary
        className={`flex min-h-[44px] cursor-pointer list-none items-center px-3.5 text-sm font-bold ${ap.heading} [&::-webkit-details-marker]:hidden`}
      >
        Field readings ›
      </summary>
      <div
        className="space-y-4 border-t px-3.5 py-3"
        style={{ borderColor: isDark ? '#2d3748' : '#e5e7eb' }}
      >
        {llm?.growth_stage_periods && llm.growth_stage_periods.length > 0 && (
          <div>
            <div className={`mb-2 text-[10px] font-bold uppercase tracking-wider ${ap.faint}`}>
              Stage timeline
            </div>
            <div className="space-y-0">
              {llm.growth_stage_periods.map((p, i) => (
                <div key={p.stage + p.tentative_start_date} className="flex gap-3">
                  <div className="flex flex-col items-center pt-1">
                    <div className={`h-2.5 w-2.5 rounded-full ${isDark ? 'bg-[#34d399]' : 'bg-[#008540]'}`} />
                    {i < llm.growth_stage_periods!.length - 1 && (
                      <div className={`my-0.5 min-h-[1.75rem] w-px flex-1 ${isDark ? 'bg-[#34d399]/30' : 'bg-[#00854033]'}`} />
                    )}
                  </div>
                  <div className={`min-w-0 flex-1 ${i < llm.growth_stage_periods!.length - 1 ? 'pb-3' : ''}`}>
                    <div className={`text-sm font-medium ${ap.heading}`}>{p.stage}</div>
                    <div className={`text-xs ${ap.faint}`}>
                      {p.tentative_start_date}
                      {p.tentative_end_date ? ` → ${p.tentative_end_date}` : ' → …'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {ndviSeries.length > 0 && (
          <div>
            <table className={`w-full text-left text-[13.5px] ${ap.muted}`}>
              <thead>
                <tr className={`border-b ${isDark ? 'border-[#2d3748]' : 'border-[#e5e7eb]'}`}>
                  <th className="py-1.5 pr-2">Date</th>
                  <th className="py-1.5 pr-2">Greenness</th>
                  <th className="py-1.5">Sky cover</th>
                </tr>
              </thead>
              <tbody>
                {ndviSeries.slice(-8).reverse().map((row) => (
                  <tr key={row.date} className={`border-b last:border-0 ${isDark ? 'border-[#2d3748]' : 'border-[#e5e7eb]'}`}>
                    <td className={`py-1.5 pr-2 ${ap.body}`}>{row.date}</td>
                    <td className={`py-1.5 pr-2 font-mono ${ap.body}`}>{row.ndvi.toFixed(2)}</td>
                    <td className="py-1.5">
                      {row.cloud_percentage != null ? `${row.cloud_percentage.toFixed(0)}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {llm?.chart_series && llm.chart_series.length > 0 && (
          <p className={`text-[13px] ${ap.faint}`}>
            Observations by date and growth stage are included in the greenness chart above.
          </p>
        )}
      </div>
    </details>
  );
}
